import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowUp } from 'lucide-react';

// Page Section Components
import HeroSection from './sections/HeroSection';
import SegmentOverview from './sections/SegmentOverview';
import PlatformFeatures from './sections/PlatformFeatures';
import LiveDataShowcase from './sections/LiveDataShowcase';
import UseCasesSection from './sections/UseCasesSection';
import AnalyticsDashboardPreview from './sections/AnalyticsDashboardPreview';
import PricingPlans from './sections/PricingPlans';
import TrialCtaSection from './sections/TrialCtaSection';
import ContactForm from './sections/ContactForm';

// Layout Components
import Footer from '../../layout/Footer/Footer.component';
import HomeHeader from '../../layout/HomeHeader/HomeHeader.component';

// --- HomePage Component ---
export default function HomePage() {
  const { i18n, t } = useTranslation();
  const user = useSelector(state => state.user.currentUser);

  // Determine text direction (RTL/LTR) based on i18n language or user preference
  const isRTL = i18n.language === 'ar' || (user?.lang === 'ar');
  const dir = isRTL ? 'rtl' : 'ltr';
  
  // Get viewport size via hooks instead of direct window access
  const [viewport, setViewport] = useState({
    isMobile: false,
    // isTablet: false, // Often, mobile and desktop breakpoints are enough for overall layout
  });

  // Update viewport size on resize
  useEffect(() => {
    const handleResize = () => {
      setViewport({
        isMobile: window.innerWidth < 768, // Adjusted breakpoint for typical tablet/mobile boundary
        // isTablet: window.innerWidth >= 768 && window.innerWidth < 1024
      });
    };
    
    // Initial call
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Framer Motion for scroll progress bar
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // State for ScrollToTopButton visibility
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      // Show button if scrolled down more than 400px
      setShowScrollTop(window.pageYOffset > 400);
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // Callback for scrolling to top
  const scrollToTop = useCallback(() => {
    const scrollAreaViewport = document.querySelector('[data-radix-scroll-area-viewport]');
    if (scrollAreaViewport) {
      scrollAreaViewport.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  // Define section order for clarity and easier reordering
  const sections = [
    { Component: HeroSection, key: 'hero' },
    { Component: SegmentOverview, key: 'segments' }, 
    { Component: PlatformFeatures, key: 'features' },
    { Component: LiveDataShowcase, key: 'showcase' },
    { Component: UseCasesSection, key: 'use-cases' },
    { Component: AnalyticsDashboardPreview, key: 'dashboard' },
    { Component: PricingPlans, key: 'pricing' },
    { Component: TrialCtaSection, key: 'cta' },
    { Component: ContactForm, key: 'contact' },
  ];

  return (
    <>
      <HomeHeader />
      {/* Scroll progress bar - improved with shadcn styling */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1 bg-primary origin-[0%] z-[100]" // Tailwind 4 origin class, higher z-index
        style={{ scaleX }} 
      />
      <ScrollArea className="h-dvh w-full bg-background"> {/* Use dvh for better mobile viewport height, consistent bg */}
        {/* Main page container with responsive margin-top and direction */}
        <div 
          dir={dir}
          className={cn(
            "flex flex-col min-h-dvh w-full",
            // "mt-16 sm:mt-20", // This margin is often better handled by the fixed header itself or body padding
            "relative isolate" // Use isolate to create a new stacking context, good for z-indexed children
          )}
        >
          {/* Animate presence for page-level transitions */}
          <AnimatePresence mode="wait">
            <motion.main // Use main for semantic HTML
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }} // Slightly faster page transition
              className="flex-1 w-full flex flex-col"
            >
              {sections.map(({ Component, key }) => (
                <Component key={key} viewport={viewport} isRTL={isRTL} />
              ))}
              
              <Footer />
            </motion.main>
          </AnimatePresence>

          {/* Scroll to top button with improved animation and shadcn styling */}
          <AnimatePresence>
            {showScrollTop && (
              <motion.div
                initial={{ opacity: 0, y: 20 }} // Subtle animation
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.25 }}
                className="fixed bottom-5 right-5 z-[90]" // Adjusted position, higher z-index
              >
                <Button
                  onClick={scrollToTop}
                  size="icon"
                  variant="outline" // Minimal style for scroll-to-top
                  className={cn(
                    "h-10 w-10 rounded-full", // Slightly smaller
                    "bg-background/70 backdrop-blur-sm hover:bg-muted",
                    "border-border/50 shadow-md hover:shadow-lg",
                    "transition-all duration-200 ease-in-out"
                  )}
                  aria-label={t('common.scrollToTop', 'Scroll to top')}
                >
                  <ArrowUp className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ScrollArea>
    </>
  );
}